import {
  BoltIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  GlobeAltIcon,
  MapPinIcon,
  NoSymbolIcon,
  PlusIcon,
  PowerIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import GPSDeviceMap from '../../components/GPSDeviceMap';
import api from '../../services/api';

interface GPSDevice {
  id: string;
  vehicle_id: string;
  device_id: string;
  provider: string;
  device_type: string;
  status: string;
  last_ping: string | null;
  battery_level: number | null;
  signal_strength: number | null;
  disable_enabled: boolean;
  installation_date: string | null;
  created_at: string;
  vehicle?: {
    id: string;
    year: number;
    make: string;
    model: string;
    vin?: string;
    stock_number?: string;
  };
}

interface GPSLocation {
  id: string;
  device_id: string;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  speed: number | null;
  ignition_on?: boolean;
  engine_running?: boolean;
  battery_voltage?: number;
  recorded_at: string;
}

interface GPSAlert {
  id: string;
  device_id: string;
  alert_type: string;
  severity: string;
  title: string;
  message: string;
  acknowledged: boolean;
  occurred_at: string;
}

interface DashboardData {
  device_stats: Record<string, number>;
  total_devices: number;
  recent_alerts: number;
  offline_devices: number;
  providers: string[];
}

const GPSDashboard: React.FC = () => {
  const [devices, setDevices] = useState<GPSDevice[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [alerts, setAlerts] = useState<GPSAlert[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<GPSDevice | null>(null);
  const [currentLocation, setCurrentLocation] = useState<GPSLocation | null>(null);
  const [locationHistory, setLocationHistory] = useState<GPSLocation[]>([]);
  const [showMap, setShowMap] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [newDevice, setNewDevice] = useState({
    device_id: '',
    provider: 'globaltrack',
    vehicle_id: '',
    serial_number: '',
  });
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGPSData();
  }, []);

  const loadGPSData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load dashboard data, devices, and recent alerts in parallel
      const [dashboardResponse, devicesResponse] = await Promise.all([
        api.get<DashboardData>('/gps/dashboard'),
        api.get<GPSDevice[]>('/gps/devices'),
      ]);

      setDashboardData(dashboardResponse);
      setDevices(devicesResponse);

      // Load recent alerts for all devices
      if (devicesResponse.length > 0) {
        const alertsPromises = devicesResponse
          .slice(0, 5)
          .map((device) =>
            api.get<GPSAlert[]>(`/gps/devices/${device.id}/alerts?limit=5&acknowledged=false`)
          );
        const alertsResults = await Promise.all(alertsPromises);
        const allAlerts = alertsResults.flat();
        setAlerts(allAlerts.slice(0, 10)); // Show top 10 alerts
      }
    } catch (err) {
      console.error('Error loading GPS data:', err);
      setError('Failed to load GPS data');
    } finally {
      setLoading(false);
    }
  };

  const loadDeviceLocation = async (deviceId: string) => {
    try {
      const location = await api.get<GPSLocation>(`/gps/devices/${deviceId}/current-location`);
      setCurrentLocation(location);
    } catch (err) {
      console.error('Error loading device location:', err);
    }
  };

  const loadDeviceHistory = async (deviceId: string) => {
    try {
      setHistoryLoading(true);
      const history = await api.get<GPSLocation[]>(`/gps/devices/${deviceId}/locations?limit=50`);
      setLocationHistory(history);
      setShowHistory(true);
    } catch (err) {
      console.error('Error loading device history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDisableDevice = async (device: GPSDevice, action: 'disable' | 'enable') => {
    try {
      await api.post(`/gps/devices/${device.id}/disable`, {
        device_id: device.id,
        action,
        reason: 'manual_override',
      });

      // Refresh devices list
      await loadGPSData();
      toast.success(`Device ${action}d successfully`);
    } catch (err) {
      console.error(`Error ${action}ing device:`, err);
      toast.error(`Failed to ${action} device`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'disabled':
        return 'bg-red-100 text-red-800';
      case 'offline':
        return 'bg-gray-100 text-gray-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatLastPing = (lastPing: string | null) => {
    if (!lastPing) return 'Never';
    const date = new Date(lastPing);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading GPS data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 mb-2">{error}</div>
          <button onClick={loadGPSData} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">GPS Tracking</h1>
          <p className="text-gray-600">Monitor and manage vehicle GPS devices</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAddDeviceModal(true)}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Device
        </button>
      </div>

      {/* Dashboard Stats */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MapPinIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Devices</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.total_devices}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <SignalIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Active Devices</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboardData.device_stats.active || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Offline Devices</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.offline_devices}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BoltIcon className="h-8 w-8 text-orange-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Recent Alerts</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.recent_alerts}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Devices List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">GPS Devices</h3>
          <p className="text-sm text-gray-500">Manage your vehicle tracking devices</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Ping
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Battery
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Signal
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {devices.map((device) => (
                <tr key={device.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <MapPinIcon className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{device.device_id}</div>
                        <div className="text-sm text-gray-500">
                          {device.vehicle
                            ? `${device.vehicle.year} ${device.vehicle.make} ${device.vehicle.model}`
                            : 'No vehicle assigned'}
                        </div>
                        <div className="text-xs text-gray-400">
                          {device.provider} • {device.device_type.replace('_', ' ')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(device.status)}`}
                    >
                      {device.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {formatLastPing(device.last_ping)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {device.battery_level ? (
                      <div className="flex items-center">
                        <div className="w-12 h-2 bg-gray-200 rounded-full mr-2">
                          <div
                            className={`h-2 rounded-full ${
                              device.battery_level > 50
                                ? 'bg-green-500'
                                : device.battery_level > 20
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                            }`}
                            style={{ width: `${device.battery_level}%` }}
                          />
                        </div>
                        {device.battery_level}%
                      </div>
                    ) : (
                      <span>N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {device.signal_strength ? (
                      <div className="flex items-center">
                        <SignalIcon className="h-4 w-4 mr-1" />
                        {device.signal_strength}%
                      </div>
                    ) : (
                      <span>N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => {
                        setSelectedDevice(device);
                        loadDeviceLocation(device.id);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    {device.disable_enabled && (
                      <button
                        onClick={() =>
                          handleDisableDevice(
                            device,
                            device.status === 'active' ? 'disable' : 'enable'
                          )
                        }
                        className={
                          device.status === 'active'
                            ? 'text-red-600 hover:text-red-900'
                            : 'text-green-600 hover:text-green-900'
                        }
                      >
                        {device.status === 'active' ? (
                          <NoSymbolIcon className="h-4 w-4" />
                        ) : (
                          <PowerIcon className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {devices.length === 0 && (
            <div className="text-center py-12">
              <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No GPS devices</h3>
              <p className="text-gray-500">Add your first GPS tracking device to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Alerts */}
      {alerts.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Recent Alerts</h3>
            <p className="text-sm text-gray-500">Unacknowledged GPS alerts</p>
          </div>

          <div className="space-y-3">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className="flex items-start justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-gray-900">{alert.title}</h4>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(alert.severity)}`}
                      >
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(alert.occurred_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  className="text-sm text-blue-600 hover:text-blue-900"
                  onClick={async () => {
                    try {
                      await api.put(`/gps/alerts/${alert.id}/acknowledge`);
                      setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
                    } catch (err) {
                      console.error('Failed to acknowledge alert:', err);
                    }
                  }}
                >
                  Acknowledge
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Device Detail Modal */}
      {selectedDevice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Device: {selectedDevice.device_id}
              </h3>
              <button
                onClick={() => {
                  setSelectedDevice(null);
                  setCurrentLocation(null);
                  setLocationHistory([]);
                  setShowMap(false);
                  setShowHistory(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {selectedDevice.vehicle && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Vehicle Information</h4>
                <p className="text-sm text-blue-700">
                  {selectedDevice.vehicle.year} {selectedDevice.vehicle.make}{' '}
                  {selectedDevice.vehicle.model}
                </p>
                {selectedDevice.vehicle.vin && (
                  <p className="text-xs text-blue-600 mt-1">VIN: {selectedDevice.vehicle.vin}</p>
                )}
                {selectedDevice.vehicle.stock_number && (
                  <p className="text-xs text-blue-600">
                    Stock: {selectedDevice.vehicle.stock_number}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Provider</label>
                <p className="mt-1 text-sm text-gray-900">{selectedDevice.provider}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedDevice.device_type.replace('_', ' ')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span
                  className={`mt-1 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedDevice.status)}`}
                >
                  {selectedDevice.status}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Ping</label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatLastPing(selectedDevice.last_ping)}
                </p>
              </div>
            </div>

            {currentLocation && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Current Location</h4>
                  <button
                    onClick={() => setShowMap(!showMap)}
                    className="text-blue-600 hover:text-blue-900 flex items-center text-sm"
                  >
                    <GlobeAltIcon className="h-4 w-4 mr-1" />
                    {showMap ? 'Hide Map' : 'View on Map'}
                  </button>
                </div>

                {showMap ? (
                  <div className="mb-4">
                    <GPSDeviceMap
                      device={selectedDevice}
                      location={currentLocation}
                      height="300px"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Latitude:</span> {currentLocation.latitude}
                    </div>
                    <div>
                      <span className="text-gray-500">Longitude:</span> {currentLocation.longitude}
                    </div>
                    <div>
                      <span className="text-gray-500">Speed:</span>{' '}
                      {currentLocation.speed ? `${currentLocation.speed} mph` : 'N/A'}
                    </div>
                    <div>
                      <span className="text-gray-500">Accuracy:</span>{' '}
                      {currentLocation.accuracy ? `${currentLocation.accuracy}m` : 'N/A'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Location History Section */}
            {showHistory && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Location History</h4>
                  <button
                    onClick={() => setShowHistory(false)}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                  >
                    Hide History
                  </button>
                </div>

                {historyLoading ? (
                  <div className="text-center py-4">
                    <div className="text-gray-500">Loading history...</div>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Time</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">
                            Location
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Speed</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {locationHistory.map((location) => (
                          <tr key={location.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                              {new Date(location.recorded_at).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                              {location.speed ? `${location.speed} mph` : 'N/A'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center space-x-1">
                                <div
                                  className={`w-2 h-2 rounded-full ${
                                    location.ignition_on ? 'bg-green-400' : 'bg-gray-400'
                                  }`}
                                />
                                <span className="text-gray-600">
                                  {location.ignition_on ? 'On' : 'Off'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => loadDeviceHistory(selectedDevice.id)}
                className="btn-secondary"
                disabled={historyLoading}
              >
                {historyLoading ? 'Loading...' : showHistory ? 'Refresh History' : 'View History'}
              </button>
              {selectedDevice.disable_enabled && (
                <button
                  onClick={() =>
                    handleDisableDevice(
                      selectedDevice,
                      selectedDevice.status === 'active' ? 'disable' : 'enable'
                    )
                  }
                  className={selectedDevice.status === 'active' ? 'btn-danger' : 'btn-primary'}
                >
                  {selectedDevice.status === 'active' ? 'Disable Device' : 'Enable Device'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Device Modal */}
      {showAddDeviceModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add GPS Device</h3>
              <button
                onClick={() => setShowAddDeviceModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Device ID *</label>
                <input
                  type="text"
                  value={newDevice.device_id}
                  onChange={(e) => setNewDevice({ ...newDevice, device_id: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="e.g. GPS-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <select
                  value={newDevice.provider}
                  onChange={(e) => setNewDevice({ ...newDevice, provider: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {(dashboardData?.providers?.length
                    ? dashboardData.providers
                    : ['globaltrack', 'passtime', 'payment_watch', 'imetrik', 'calamp']
                  ).map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serial Number
                </label>
                <input
                  type="text"
                  value={newDevice.serial_number}
                  onChange={(e) => setNewDevice({ ...newDevice, serial_number: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddDeviceModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!newDevice.device_id.trim()) return;
                  try {
                    await api.post('/gps/devices', {
                      device_id: newDevice.device_id,
                      provider: newDevice.provider,
                      serial_number: newDevice.serial_number || undefined,
                      vehicle_id: newDevice.vehicle_id || undefined,
                    });
                    setShowAddDeviceModal(false);
                    setNewDevice({
                      device_id: '',
                      provider: 'globaltrack',
                      vehicle_id: '',
                      serial_number: '',
                    });
                    loadGPSData();
                  } catch (err) {
                    console.error('Failed to add device:', err);
                  }
                }}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Add Device
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GPSDashboard;
